import { Notifications } from '@mui/icons-material';
import { Badge, Box, Button, IconButton, ListItemButton, Popover, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { JSX, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { myAppointmentsLink, profileLink } from '../../library/constants';
import { formatRecordDateTime } from '../../library/dateTimeHelper';
import { IAppointment, IPendingRequestCount } from '../../library/models/appointment';
import { localised } from '../../library/models/localisedText';
import { INotification, IUnreadCount, notificationTitleKey } from '../../library/models/notification';
import { needsHomeScreen, pushSupported } from '../../library/pushSubscription';
import { usePushSync } from '../../library/usePushSync';
import { useRefreshDiary } from '../../library/useRefreshDiary';
import ResourcesIds from '../../resources/ResourcesIds';
import { IPushKey } from '../../services/pushService';
import { useReactInfiniteQuery, useReactMutation, useReactQuery } from '../../services/queryService';
import { services } from '../../services/services';
import { AppointmentDetailDialog } from '../appointmentDetail/AppointmentDetailDialog';
import { anchorOrigin, transformOrigin } from '../layout/header.styles';
import { useSnackbar } from '../layout/snackbarProvider';
import { PendingRequests } from '../pages/MyCalendar/PendingRequests';
import { PersonLabel } from '../userAvatar/UserAvatar';
import {
    bellEmptyStyles,
    bellFooterStyles,
    bellHeaderStyles,
    bellListStyles,
    bellPaperStyles,
    bellRowBodyStyles,
    bellRowDetailStyles,
    bellRowStampStyles,
    bellRowWhenStyles,
    bellShowMoreStyles,
    bellTitleStyles,
    getBellDotStyles,
    getBellRowStyles,
    getBellRowTitleStyles,
} from './notificationBell.styles';

/** How many lines a page holds; "show more" fetches the next from the server. */
export const NOTIFICATIONS_PAGE_SIZE = 10;

/** How often the count is asked for while a tab is open. A booking is not a chat message. */
const POLL_MS = 60_000;

/**
 * The bell in the header: what has happened to the signed-in person's appointments that
 * somebody else -- or the clock -- did, and for a therapist, what is waiting for their answer.
 *
 * **Two different things share the one number.** A booking request is a *state*: it is
 * counted for as long as it is unanswered, read from the appointments themselves, and cannot
 * be marked read. Everything under it is *news*: it happened, and it stays unread until it
 * is opened. The badge is their sum, and the server never writes a request as news, so
 * nothing is counted twice.
 *
 * **The count is polled; the list is not.** The list is asked for only while the panel is
 * open, a page at a time, and its "show more" is a real request.
 *
 * **What a line opens depends on which side of the appointment the reader is on**, which the
 * line itself says. A patient is taken to My Appointments, where moving and withdrawing
 * already live. A therapist gets the appointment's own dialog, fetched fresh by id -- a line
 * may be about a week that is not on screen, or about nothing the calendar has loaded.
 */
export const NotificationBell = (): JSX.Element => {
    const { t: translate, i18n } = useTranslation();
    const navigate = useNavigate();
    const { role } = useAuth();
    const { showSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const refreshDiary = useRefreshDiary();

    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [opened, setOpened] = useState<IAppointment | null>(null);

    const isOpen: boolean = anchor !== null;
    const isTherapist: boolean = role === 'THERAPIST';

    const { data: unread } = useReactQuery<IUnreadCount>(
        services.notifications.getUnreadCount(),
        { refetchInterval: POLL_MS }
    );

    // Only the therapist a request names may answer it, so nobody else is asked.
    const { data: pending } = useReactQuery<IPendingRequestCount>(
        services.appointments.getPendingRequestCount(),
        { enabled: isTherapist, refetchInterval: POLL_MS }
    );

    const { data, hasNextPage, fetchNextPage, isFetchingNextPage, isSuccess } =
        useReactInfiniteQuery<INotification>(
            services.notifications.getNotifications(NOTIFICATIONS_PAGE_SIZE),
            { enabled: isOpen }
        );

    // The same news can reach this device when the app is closed. Whether to say so depends
    // on the deployment having push switched on and on this browser being able to do it --
    // asked only once the panel is open, and once: the answer does not change in a session.
    const canOfferPush: boolean = pushSupported() || needsHomeScreen();

    const { data: pushKey } = useReactQuery<IPushKey | null>(
        services.push.getKey(),
        { enabled: isOpen && canOfferPush, staleTime: Infinity }
    );

    usePushSync();

    const markRead = useReactMutation(services.notifications.markReadMutation());
    const markAllRead = useReactMutation(services.notifications.markAllReadMutation());

    const notifications: INotification[] = data?.pages.flatMap(page => page.items) ?? [];
    const total: number = data?.pages[0]?.totalItems ?? 0;

    const unreadCount: number = unread?.count ?? 0;
    const pendingCount: number = isTherapist ? pending?.count ?? 0 : 0;
    const badgeCount: number = unreadCount + pendingCount;

    /**
     * By name, each of them: React Query matches a key by its leading element, and
     * `['/notifications']` does not reach `['/notifications/unread-count']`.
     */
    const refreshBell = useCallback((): void => {
        void queryClient.invalidateQueries({ queryKey: ['/notifications'] });
        void queryClient.invalidateQueries({ queryKey: ['/notifications/unread-count'] });
    }, [queryClient]);

    const close = (): void => setAnchor(null);

    /**
     * Opening the panel is somebody asking what is new, so the two numbers are asked for again
     * rather than left as the minute-old ones the poll last saw. Without this a request that
     * arrived since can be listed in the panel under a badge that still says nothing.
     */
    const open = (target: HTMLElement): void => {
        refreshBell();
        void queryClient.invalidateQueries({ queryKey: ['/appointments/requests/count'] });
        setAnchor(target);
    };

    const openAppointment = (appointmentId: number): void => {
        // Fetched now rather than read from a cache: the buttons the dialog offers depend on
        // what the appointment is at this moment, not on what it was when last looked at.
        queryClient
            .fetchQuery({ ...services.appointments.getAppointment(appointmentId), staleTime: 0 })
            .then((appointment: IAppointment) => setOpened(appointment))
            .catch(() => showSnackbar(translate(ResourcesIds.NOTIFICATIONS_OPEN_FAILED), 'error'));
    };

    const handleRowClick = (notification: INotification): void => {
        if (!notification.read) {
            markRead.mutate(notification.id, { onSuccess: refreshBell });
        }

        // Whatever the line is about has changed since the page behind it was drawn.
        refreshDiary();
        close();

        if (notification.audience === 'PATIENT') {
            navigate(myAppointmentsLink);
            return;
        }

        openAppointment(notification.appointmentId);
    };

    const bellLabel: string = badgeCount > 0
        ? translate(ResourcesIds.NOTIFICATIONS_BELL_LABEL_COUNT, { count: badgeCount })
        : translate(ResourcesIds.NOTIFICATIONS_TITLE);

    return (
        <>
            <IconButton
                color="inherit"
                aria-label={bellLabel}
                aria-haspopup="dialog"
                onClick={(event) => open(event.currentTarget)}
            >
                <Badge badgeContent={badgeCount} color="secondary" max={99}>
                    <Notifications />
                </Badge>
            </IconButton>

            <Popover
                open={isOpen}
                anchorEl={anchor}
                onClose={close}
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
                slotProps={{ paper: { sx: bellPaperStyles } }}
            >
                <Box sx={bellHeaderStyles}>
                    <Typography component="h2" sx={bellTitleStyles}>
                        {translate(ResourcesIds.NOTIFICATIONS_TITLE)}
                    </Typography>
                    <Button
                        size="small"
                        disabled={unreadCount === 0 || markAllRead.isPending}
                        onClick={() => markAllRead.mutate(undefined, { onSuccess: refreshBell })}
                    >
                        {translate(ResourcesIds.NOTIFICATIONS_MARK_ALL_READ)}
                    </Button>
                </Box>

                {isTherapist && <PendingRequests embedded />}

                {isSuccess && notifications.length === 0 && pendingCount === 0 && (
                    <Typography sx={bellEmptyStyles}>
                        {translate(ResourcesIds.NOTIFICATIONS_EMPTY)}
                    </Typography>
                )}

                <Box sx={bellListStyles}>
                    {notifications.map(notification => (
                        <ListItemButton
                            key={notification.id}
                            sx={getBellRowStyles(notification.read)}
                            data-testid="notification"
                            onClick={() => handleRowClick(notification)}
                        >
                            <Box
                                sx={getBellDotStyles(notification.read)}
                                role={notification.read ? undefined : 'img'}
                                aria-label={notification.read
                                    ? undefined
                                    : translate(ResourcesIds.NOTIFICATIONS_UNREAD)}
                            />
                            <Box sx={bellRowBodyStyles}>
                                <Typography sx={getBellRowTitleStyles(notification.read)}>
                                    {translate(notificationTitleKey(notification))}
                                </Typography>
                                <Typography sx={bellRowWhenStyles}>
                                    {formatRecordDateTime(notification.startsAt, i18n)}
                                </Typography>
                                {notification.previousStartsAt && (
                                    <Typography sx={bellRowDetailStyles}>
                                        {translate(ResourcesIds.NOTIFICATION_MOVED_FROM, {
                                            when: formatRecordDateTime(notification.previousStartsAt, i18n),
                                        })}
                                    </Typography>
                                )}
                                {notification.otherPartyName && (
                                    <PersonLabel
                                        size="sm"
                                        person={{
                                            name: notification.otherPartyName,
                                            pictureUrl: notification.otherPartyPictureUrl,
                                        }}
                                    />
                                )}
                                {notification.serviceNameRo && (
                                    <Typography sx={bellRowDetailStyles}>
                                        {localised(notification.serviceNameRo,
                                            notification.serviceNameEn, i18n.language)}
                                    </Typography>
                                )}
                                {notification.decisionNote && (
                                    <Typography sx={bellRowDetailStyles}>
                                        {translate(ResourcesIds.APPOINTMENT_DECLINED_NOTE, {
                                            note: notification.decisionNote,
                                        })}
                                    </Typography>
                                )}
                                <Typography sx={bellRowStampStyles}>
                                    {formatRecordDateTime(notification.createdAt, i18n)}
                                </Typography>
                            </Box>
                        </ListItemButton>
                    ))}
                </Box>

                {hasNextPage && (
                    <Box sx={bellShowMoreStyles}>
                        <Button disabled={isFetchingNextPage} onClick={() => fetchNextPage()}>
                            {translate(ResourcesIds.MY_APPOINTMENTS_SHOW_MORE, {
                                remaining: Math.max(total - notifications.length, 0),
                            })}
                        </Button>
                    </Box>
                )}

                {canOfferPush && pushKey && (
                    <Box sx={bellFooterStyles}>
                        <Button
                            size="small"
                            onClick={() => {
                                close();
                                navigate(profileLink);
                            }}
                        >
                            {translate(ResourcesIds.PUSH_BELL_LINK)}
                        </Button>
                    </Box>
                )}
            </Popover>

            {/* Beside the panel rather than inside it: the panel has closed by the time the
                appointment arrives, and a dialog that lived in it would have gone with it. The
                dialog refreshes the diary itself after every verb, so there is nothing for
                this host to refetch. */}
            <AppointmentDetailDialog
                appointment={opened}
                onClose={() => setOpened(null)}
                onChanged={() => undefined}
            />
        </>
    );
};
